import { AxiosError, AxiosResponse } from "axios";

import { AddCartItemPayload, Cart, LineSubscriptionUpdate } from "@/types/cart";
import { getRequest, postRequest } from "@/utils/axios";
import { orderMetrics } from "@/utils/newrelicHelpers";

export type RawCart = Omit<Cart, "line_items"> & {
  line_items?: Cart["line_items"];
};
// Defaults to normalizeCart; a caller that must not mistake a truncated cart for an
// empty one passes a stricter parser.
type ParseCart = (cart?: RawCart) => Cart | undefined;

// The API always includes line_items, but a partial/interrupted response can omit
// it. Normalize at the fetch boundary so every cart consumer can rely on the array.
// Deliberately scoped to line_items (the field behind the crash); every other field
// passes through untouched.
export const normalizeCart = (cart?: RawCart): Cart | undefined => {
  if (!cart) return undefined;
  if (cart.line_items == null) {
    orderMetrics.logCartMissingLineItems(cart.id, cart.status);
  }
  return { ...cart, line_items: cart.line_items ?? [] };
};

const getOrCreateCart = async (
  parseCart: ParseCart = normalizeCart
): Promise<Cart | undefined> => {
  const response = await postRequest(
    "/api/v1/cart/",
    {},
    (response: AxiosResponse) => {
      return parseCart(response.data);
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
};

const getLatestActiveCart = async (): Promise<Cart | undefined> => {
  const response = await getRequest(
    "/api/v1/cart/",
    (response: AxiosResponse) => {
      if (response.data?.length > 0) {
        return normalizeCart(response.data[0]);
      }
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
};

const fetchCart = async (
  cartId: string,
  parseCart: ParseCart = normalizeCart
): Promise<Cart | undefined> => {
  const response = await getRequest(
    `/api/v1/cart/${cartId}/`,
    (response: AxiosResponse) => {
      return parseCart(response.data);
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
};

// offerId: the chat program card's "treatment-program" makes the batch all-or-nothing (S12).
const addItemsToCart = async (
  cartId: string,
  items: AddCartItemPayload[],
  parseCart: ParseCart = normalizeCart,
  offerId?: string
): Promise<Cart | undefined> => {
  const response = await postRequest(
    `/api/v1/cart/${cartId}/items/`,
    offerId ? { items, offer_id: offerId } : { items },
    (response: AxiosResponse) => {
      return parseCart(response.data);
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
};

const removeItemsFromCart = async (
  cartId: string,
  itemIds: string[]
): Promise<Cart | undefined> => {
  const response = await postRequest(
    `/api/v1/cart/${cartId}/items/delete/`,
    { item_ids: itemIds },
    (response: AxiosResponse) => {
      return normalizeCart(response.data);
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
};

// Persists subscription choices on existing lines. Not an add-to-cart, so the
// server fires none of those events.
const setItemsSubscription = async (
  cartId: string,
  items: LineSubscriptionUpdate[]
): Promise<Cart | undefined> => {
  const response = await postRequest(
    `/api/v1/cart/${cartId}/items/subscription/`,
    { items },
    (response: AxiosResponse) => {
      return normalizeCart(response.data);
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
};

const createCartCheckout = async (
  cartId: string,
  consultId?: string,
  purchaseType?: string,
  checkoutSource?: string
) => {
  // For OTC-only carts, consult and purchase info should be empty
  const requestData: {
    consult_uid?: string;
    purchase_type?: string;
    checkout_source?: string;
  } = {};

  if (consultId) {
    requestData.consult_uid = consultId;
  }

  if (purchaseType) {
    requestData.purchase_type = purchaseType;
  }

  if (checkoutSource) {
    requestData.checkout_source = checkoutSource;
  }

  const response = await postRequest(
    `/api/v1/cart/${cartId}/checkout/`,
    requestData,
    (response: AxiosResponse) => {
      return response.data;
    },
    (error: AxiosError) => {
      throw error;
    }
  );
  return response;
};

export const cartService = {
  getOrCreateCart,
  addItemsToCart,
  fetchCart,
  removeItemsFromCart,
  setItemsSubscription,
  createCartCheckout,
  getLatestActiveCart,
};
